import { useUiLanguage } from "@/i18n/useUiLanguage";
import { systemText, tr, uiLocale, localizedStatic } from "@/i18n/copy";
import { currencyCode, currencyLabel } from "@/lib/money";
import WorkflowGuide, { useWorkflowGuide } from "@/components/WorkflowGuide";
import CustomerTag from "@/components/CustomerTag";
import { SERVICE_LABELS, appointmentServiceLabel } from "@/serviceTypes";
import { useEffect, useRef, useState } from "react";
import CheckoutPanel from "@/components/CheckoutPanel";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, CameraOff, QrCode, UserRound, ShieldCheck, Smartphone, MonitorSmartphone, CheckCircle2, CircleAlert, ChevronDown, WalletCards, Handshake } from "lucide-react";

const BASE_MODES = localizedStatic([
  ["client", "QR ID Cliente", UserRound],
  ["pro", "QR ID Pro", ShieldCheck],
], [[0,1],[1,1]]);

const QR_LIB_URL = "https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js";
let qrLibraryPromise = null;

function loadQrLibrary() {
  if (typeof window === "undefined") return Promise.reject(new Error(tr("Browser non disponibile")));
  if (window.Html5Qrcode) return Promise.resolve(window.Html5Qrcode);
  if (qrLibraryPromise) return qrLibraryPromise;

  qrLibraryPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("tps-html5-qrcode-lib");
    const done = () => window.Html5Qrcode ? resolve(window.Html5Qrcode) : reject(new Error(tr("Lettore QR non disponibile")));
    if (existing) {
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", () => reject(new Error(tr("Impossibile caricare il lettore QR"))), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = "tps-html5-qrcode-lib";
    script.src = QR_LIB_URL;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = done;
    script.onerror = () => reject(new Error(tr("Impossibile caricare il lettore QR")));
    document.head.appendChild(script);
  }).catch((error) => {
    qrLibraryPromise = null;
    throw error;
  });

  return qrLibraryPromise;
}

const durationLabel = (minutes) => {
  if (!minutes) return "—";
  const hours = Number(minutes) / 60;
  return Number.isInteger(hours) ? tr("{{0}} h", {"0": hours}) : tr("{{0}} h", {"0": String(hours).replace(".", ",")});
};


function cameraErrorMessage(error) {
  if (!window.isSecureContext) return tr("La fotocamera richiede una connessione HTTPS sicura.");
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return tr("Permesso fotocamera negato. Abilita la fotocamera nelle impostazioni del browser e riprova.");
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return tr("Nessuna fotocamera disponibile su questo dispositivo. Puoi usare lo Scanner mobile.");
  if (name === "NotReadableError" || name === "TrackStartError") return tr("La fotocamera è già in uso da un'altra app o scheda. Chiudila e riprova.");
  if (!window.isSecureContext) return tr("La fotocamera richiede una connessione HTTPS sicura.");
  return errMsg(error) || tr("Impossibile avviare la fotocamera.");
}

export default function ScannerQR() {
  useUiLanguage();
  const { user } = useAuth();
  const canHandlePayments = user?.role === "studio_owner";
  const modes = user?.role === "artist" ? [BASE_MODES[0], ["agreement", tr("Accordo economico"), Handshake]] : BASE_MODES;
  const scannerRef = useRef(null); const pollRef = useRef(null);
  const cameraAttempt = useRef(0); const cameraBusy = useRef(false);
  const [cameraError, setCameraError] = useState("");
  const [active, setActive] = useState(false); const [starting, setStarting] = useState(false); const [mode, setMode] = useState("client");
  const [code, setCode] = useState(""); const [result, setResult] = useState(null); const [resultKind, setResultKind] = useState(null); const [loading, setLoading] = useState(false);
  const workflowGuide = useWorkflowGuide(resultKind === "ownership" ? "scanner-ownership" : resultKind === "agreement" || mode === "agreement" ? "scanner-agreement" : "scanner-checkin");
  const [relay, setRelay] = useState(null); const [mobileRelay, setMobileRelay] = useState(null);
  const cameraCapable = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const stop = async () => {
    cameraAttempt.current += 1;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { if (scanner.isScanning) await scanner.stop(); } catch {}
      try { await scanner.clear(); } catch {}
    }
    setActive(false);
    setStarting(false);
  };
  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  useEffect(()=>()=>{ stop(); stopPolling(); },[]);
  useEffect(() => { setCode(""); setResult(null); setResultKind(null); stop(); }, [mode]);
  useEffect(() => {
    const requestedMode = new URLSearchParams(window.location.search).get("mode");
    if (requestedMode === "agreement" && user?.role === "artist") setMode("agreement");
    api.get("/scanner-relay/active").then((r) => {
      if (r?.active && requestedMode !== "agreement") { setMobileRelay(r); setMode(r.mode || "client"); }
    }).catch(() => {});
  }, [user?.role]);

  const verify = async (raw = code, forcedMode = mode) => {
    const value = String(raw || "").trim(); if (!value || loading) return;
    setLoading(true); setResult(null);
    try {
      if (value.startsWith("TPS-CUSTOMER-OWNERSHIP:")) {
        const preview = await api.post("/customer-ownership/qr/preview", {qr_value:value});
        setResult({...preview,qr_value:value});setResultKind("ownership");
      } else if (value.startsWith("TPS-ECONOMIC-AGREEMENT:")) {
        if (user?.role !== "artist") throw new Error(tr("L’accordo deve essere verificato dal professionista interessato."));
        const preview = await api.post("/artist-self/economic-agreement/preview", { qr_value: value });
        setResult({ ...preview, qr_value: value }); setResultKind("agreement");
        toast.success(tr("Proposta economica verificata"));
      } else {
        const resolved = await api.post("/identity-scanner/resolve", { value });
        setResult(resolved.result); setResultKind(resolved.kind);
        if (resolved.kind === "client") {
          const gate = resolved.result?.checkin;
          if (gate?.status === "completed") toast.success(tr("Cliente già in check-in"));
          else if (gate?.ready) toast.success(tr("CHECK-IN OK"), { description: tr("Tutti i requisiti per l'appuntamento di oggi sono completi.") });
          else toast.warning(tr("CHECK-IN NON COMPLETO"), { description: tr("Controlla i requisiti mancanti prima di procedere.") });
        } else {
          let presence = null;
          try { presence = await api.get(`/team-presence/member/${resolved.result.id}`); } catch {}
          setResult({ ...resolved.result, presence }); setResultKind("pro");
          toast.success(tr("Identità Pro verificata"));
        }
      }
    } catch(e){ toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const acceptOwnership = async () => {
    if(!result?.qr_value||loading)return;
    setLoading(true);
    try {
      const response=await api.post("/customer-ownership/qr/accept",{qr_value:result.qr_value});
      workflowGuide.complete();
      toast.success(response.message);setResult(null);setCode("");
      window.dispatchEvent(new Event("tps:data-changed"));
    } catch(e){toast.error(errMsg(e));}finally{setLoading(false);}
  };

  const acceptAgreement = async () => {
    if (!result?.qr_value || loading) return;
    setLoading(true);
    try {
      await api.post("/artist-self/economic-agreement/accept", { qr_value: result.qr_value });
      workflowGuide.complete();
      toast.success(tr("Accordo economico approvato"), { description: tr("La nuova versione è ora valida per i futuri incassi.") });
      setResult(null); setCode("");
      window.dispatchEvent(new Event("tps:data-changed"));
    } catch (error) { toast.error(errMsg(error)); }
    finally { setLoading(false); }
  };
  const rejectAgreement = async () => {
    if (!result?.proposal_id || loading) return;
    if (!window.confirm(tr("Rifiutare questa proposta economica? L'accordo attuale resterà invariato."))) return;
    setLoading(true);
    try {
      await api.post(`/artist-self/economic-agreement/reject/${result.proposal_id}`, {});
      workflowGuide.complete();
      toast.success(tr("Proposta rifiutata"));
      setResult(null); setCode("");
      window.dispatchEvent(new Event("tps:data-changed"));
    } catch (error) { toast.error(errMsg(error)); }
    finally { setLoading(false); }
  };

  const confirmCheckin = async () => {
    const value = String(code || "").trim();
    if (!value || loading) return;
    setLoading(true);
    try {
      const checked = await api.post("/identity-scanner/client/check-in", { value });
      if (checked.ok) workflowGuide.complete();
      setResult(checked.result); setResultKind("client");
      window.dispatchEvent(new CustomEvent("tps:data-changed", { detail: { source: "client-checkin" } }));
      if (checked.ok) toast.success(tr("Check-in completato"), { description: tr("Ingresso Cliente collegato all'appuntamento di oggi.") });
      else toast.error(tr("Check-in non completo"), { description: tr("Risolvi i requisiti indicati e riprova.") });
    } catch (error) { toast.error(errMsg(error)); }
    finally { setLoading(false); }
  };

  const confirmProPresence = async (action) => {
    const value = String(code || "").trim();
    if (!value || loading) return;
    setLoading(true);
    try {
      const response = await api.post("/team-presence/scan", { qr_value: value, action });
      setResult((current)=>({ ...(current||{}), presence: response.current }));
      toast.success(action === "check_in" ? tr("Check-in Team registrato") : tr("Check-out Team registrato"));
      window.dispatchEvent(new CustomEvent("tps:data-changed", { detail: { source: "team-presence" } }));
    } catch (error) { toast.error(errMsg(error)); }
    finally { setLoading(false); }
  };

  const confirmCashClose = async () => {
    const value = String(code || "").trim();
    if (!value || loading) return;
    if (!window.confirm(tr("Registrare la chiusura cassa con questo QR ID Pro?"))) return;
    setLoading(true);
    try {
      await api.post("/team-presence/cash-close", { qr_value: value, notes: "" });
      toast.success(tr("Chiusura cassa registrata"));
      window.dispatchEvent(new CustomEvent("tps:data-changed", { detail: { source: "cash-close" } }));
    } catch (error) { toast.error(errMsg(error)); }
    finally { setLoading(false); }
  };

  const onScanned = async (value) => {
    const raw = String(value || "").trim();
    if (!raw) return;
    setCode(raw);
    await stop();
    if (mobileRelay?.active) {
      try { await api.post(`/scanner-relay/sessions/${mobileRelay.id}/submit`, { value: raw }); toast.success(tr("Scansione inviata al dispositivo principale")); setMobileRelay(null); }
      catch (e) { toast.error(errMsg(e)); }
    } else {
      toast.success(tr("QR letto"));
      await verify(raw, mode);
    }
  };

  const start = async () => {
    if (cameraBusy.current || active) return;
    cameraBusy.current = true;
    const attempt = ++cameraAttempt.current;
    const current = () => cameraAttempt.current === attempt;
    let scanner = null;
    let observer = null;
    setCameraError(""); setStarting(true);
    try {
      if (!window.isSecureContext) throw new Error(tr("La fotocamera richiede HTTPS"));
      if (!cameraCapable) throw Object.assign(new Error(tr("Fotocamera non disponibile")), { name: "NotFoundError" });
      const Html5Qrcode = await loadQrLibrary();
      if (!current()) return;
      const reader = document.getElementById("tps-qr-reader");
      // Keep the preview visible before the library measures it or starts playback.
      reader.style.display = "block";
      const prepareVideo = () => {
        const video = reader.querySelector("video");
        if (!video) return;
        video.muted = true; video.defaultMuted = true; video.playsInline = true;
        video.setAttribute("playsinline", ""); video.setAttribute("webkit-playsinline", "");
        video.setAttribute("muted", "");
        video.style.width = "100%"; video.style.objectFit = "cover";
      };
      observer = new MutationObserver(prepareVideo);
      observer.observe(reader, { childList: true, subtree: true });
      scanner = new Html5Qrcode("tps-qr-reader", { verbose: false });
      scannerRef.current = scanner;
      // One camera acquisition: opening and immediately stopping a permission
      // stream before opening it again can leave iOS with a black preview.
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (width, height) => { const side = Math.min(250, Math.floor(Math.min(width, height) * 0.8)); return { width: side, height: side }; } },
        (decodedText) => { if (current()) onScanned(decodedText); },
        () => {},
      );
      if (!current()) return;
      prepareVideo();
      const video = reader.querySelector("video");
      if (!video) throw new Error(tr("Anteprima fotocamera non disponibile. Premi Scansiona qui per riprovare."));
      await video.play();
      const deadline = Date.now() + 8000;
      while (current() && (!video.videoWidth || video.readyState < 2 || video.paused)) {
        if (Date.now() >= deadline) throw new Error(tr("La fotocamera non invia immagini. Chiudi le altre app che la usano, poi premi Scansiona qui."));
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (current()) { setActive(true); setStarting(false); }
    } catch (e) {
      if (current()) {
        const message = cameraErrorMessage(e);
        setCameraError(message); toast.error(message);
        await stop();
      }
    } finally {
      observer?.disconnect();
      if (!current() && scanner) {
        try { if (scanner.isScanning) await scanner.stop(); } catch {}
        try { scanner.clear(); } catch {}
      }
      cameraBusy.current = false;
    }
  };

  const startRelay = async () => {
    try {
      const session = await api.post("/scanner-relay/sessions", { mode }); setRelay(session); stopPolling();
      toast.success(tr("Scanner mobile attivo per 10 minuti"));
      pollRef.current = setInterval(async () => {
        try {
          const state = await api.get(`/scanner-relay/sessions/${session.id}`);
          if (state.status === "received") {
            stopPolling(); const consumed = await api.post(`/scanner-relay/sessions/${session.id}/consume`, {});
            setRelay(null); setCode(consumed.value); await verify(consumed.value, consumed.mode || mode);
          }
        } catch { stopPolling(); setRelay(null); }
      }, 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const closeRelay = async () => { stopPolling(); if (relay?.id) api.post(`/scanner-relay/sessions/${relay.id}/close`, {}).catch(()=>{}); setRelay(null); };

  return <div className="animate-fade-up max-w-5xl"><PageHeader subtitle={user?.role === "artist" ? tr("Scansiona il QR Cliente per il check-in oppure il QR dello Studio per leggere e accettare un accordo economico.") : tr("Un solo ingresso operativo: QR ID Cliente per check-in, Wallet e checkout della seduta; QR ID Pro per verificare il Team. Bonus e Gift Card vengono applicati solo nel checkout Cliente.")} title={tr("Scanner QR")}/>{mode !== "pro" && <WorkflowGuide guide={workflowGuide} className="mb-4" current={!result ? 0 : result?.checkin?.ready ? 2 : 1}/>}
    {mobileRelay?.active && <Card className="mb-5 border-primary/40 bg-primary/10 p-4"><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div><div className="font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary"/>{tr("Richiesta di scansione dal dispositivo principale")}</div><p className="text-sm text-muted-foreground mt-1">{tr("Modalità:")} {mobileRelay.mode === "client" ? tr("QR ID Cliente") : tr("QR ID Pro")}{tr(". Inquadra il QR: il risultato comparirà automaticamente anche sul Mac/PC.")}</p></div><Button onClick={start} disabled={starting || active}><Camera className="h-4 w-4 mr-2"/>{starting ? tr("Apertura…") : tr("Apri fotocamera")}</Button></div></Card>}
    <div className="flex flex-wrap gap-2 mb-5">{modes.map(([id,label,Icon]) => <Button key={id} variant={mode===id?"default":"outline"} onClick={()=>setMode(id)}><Icon className="w-4 h-4 mr-2"/>{label}</Button>)}</div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 bg-card/60"><div className="label-tiny mb-4 flex items-center gap-2"><Camera className="w-4 h-4"/>{tr("Scansione")}</div><div className="aspect-square rounded-lg overflow-hidden bg-black grid place-items-center relative"><div id="tps-qr-reader" className={`w-full h-full ${active || starting ? "block" : "hidden"}`} />{!active && !starting && <div className="text-center text-muted-foreground p-6 absolute inset-0 grid place-items-center"><div><QrCode className="w-10 h-10 mx-auto mb-3"/><div className="text-sm">{cameraCapable ? tr("Inquadra {{0}}", {"0": mode === "client" ? tr("il QR ID Cliente") : mode === "agreement" ? tr("il QR dell’accordo economico") : tr("il QR ID Pro")}) : !window.isSecureContext ? tr("La fotocamera richiede una connessione HTTPS sicura.") : tr("Nessuna fotocamera rilevata: usa Scanner mobile")}</div></div></div>}{starting && <div className="absolute inset-0 grid place-items-center pointer-events-none"><div className="rounded-lg bg-black/70 px-4 py-2 text-sm text-white">{tr("Attivazione fotocamera…")}</div></div>}</div>
        {cameraError && <p role="alert" className="mt-3 text-sm text-destructive">{cameraError}</p>}
        <div className="flex flex-wrap gap-2 mt-4">{active?<Button variant="outline" onClick={stop}><CameraOff className="w-4 h-4 mr-2"/>{tr("Ferma")}</Button>:<Button onClick={start} disabled={starting}><Camera className="w-4 h-4 mr-2"/>{starting ? tr("Apertura…") : tr("Scansiona qui")}</Button>}{mode!=="agreement"&&<Button variant="outline" onClick={relay ? closeRelay : startRelay}><MonitorSmartphone className="w-4 h-4 mr-2"/>{relay ? tr("Annulla scanner mobile") : tr("Usa fotocamera del telefono")}</Button>}</div>
        <p className="mt-3 text-xs text-muted-foreground">{mode==="agreement"?tr("Per l'accordo economico usa direttamente il dispositivo autenticato dell'Artista: il QR deve essere mostrato dall'app dello Studio e letto di persona dall'Artista."):tr("Su iPhone/iPad il browser deve chiedere il permesso fotocamera al primo utilizzo. Se il dispositivo corrente non ha una fotocamera utilizzabile, attiva Scanner mobile sul dispositivo principale e apri TPS sul telefono con lo stesso account.")}</p>
        {relay && <div className="mt-4 rounded-xl border border-primary/30 bg-primary/10 p-4"><div className="flex items-center gap-2 font-semibold"><Smartphone className="w-4 h-4 text-primary"/>{tr("In attesa del telefono…")}</div><p className="mt-1 text-sm text-muted-foreground">{tr("Apri Tattoo Pro Studio sul telefono con")} <b>{tr("lo stesso account")}</b> {tr("e vai su Scanner QR. La richiesta comparirà automaticamente. La sessione scade tra 10 minuti.")}</p></div>}
      </Card>
      <Card className="p-5 bg-card/60"><div className="label-tiny mb-4">{tr("Verifica smart")}</div><div className="space-y-4"><div><Label className="label-tiny">{tr("Codice")}</Label><Input value={code} onChange={e=>setCode(e.target.value)} placeholder={tr("Incolla o scansiona il QR")} className="mt-1.5"/></div><Button className="w-full" disabled={loading || !code.trim()} onClick={()=>verify()}>{loading ? tr("Verifica…") : mode === "client" ? tr("Verifica check-in") : mode === "agreement" ? tr("Verifica accordo") : tr("Verifica identità")}</Button></div>
        {result && resultKind === "client" && <ClientResult data={result} onCheckin={confirmCheckin} loading={loading} canHandlePayments={canHandlePayments}/>}
        {result && resultKind === "pro" && <ProResult data={result} loading={loading} onPresence={confirmProPresence} onCashClose={confirmCashClose}/>}
        {result && resultKind === "ownership" && <div className="mt-5 space-y-3 rounded-xl border border-primary/30 p-4"><h3 className="font-semibold">{tr("Verifica modifica associazione ·")} {result.customer_name}</h3><p>{tr("Attuale:")} {result.pending.before_label}</p><p>{tr("Proposta:")} <b>{result.pending.after_label}</b></p><p>{tr("Motivo:")} {result.pending.reason}</p><p className="text-xs">{tr("La modifica sarà applicata solo dopo tutte le conferme. Gli incassi precedenti restano invariati.")}</p><Button disabled={loading} onClick={acceptOwnership}>{tr("Confermo questa modifica")}</Button><Button variant="outline" disabled={loading} onClick={()=>{setResult(null);setCode("");}}>{tr("Non confermare")}</Button></div>}
        {result && resultKind === "agreement" && <AgreementResult data={result} loading={loading} onAccept={acceptAgreement} onReject={rejectAgreement}/>}
      </Card>
    </div>
  </div>;
}

function ClientResult({ data, onCheckin, loading, onRefresh, canHandlePayments }) {
  useUiLanguage();
 const moneyCurrency = currencyCode(useAuth().studio);
  const a = data.appointment_today || data.next_appointment;
  const gate = data.checkin || {};
  const completed = gate.status === "completed";
  const ready = Boolean(gate.ready);
  const statusTitle = completed ? tr("CHECK-IN ESEGUITO") : ready ? tr("CHECK-IN OK") : tr("CHECK-IN NON COMPLETO");
  const statusClass = completed || ready ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/50 bg-destructive/10";
  const StatusIcon = completed || ready ? CheckCircle2 : CircleAlert;
  const statusIconClass = completed || ready ? "text-emerald-500" : "text-destructive";
  const ageLabel = gate.age_status === "adult" ? tr("Maggiorenne") : gate.age_status === "minor" ? tr("Minorenne") : tr("Da verificare");
  const requirements = gate.requirements || {};

  return <div className="mt-5 space-y-3" data-testid="client-scan-result">
    <div className={`rounded-xl border p-4 ${statusClass}`}>
      <div className="flex items-start gap-3"><StatusIcon className={`mt-0.5 h-5 w-5 shrink-0 ${statusIconClass}`}/><div><div className="font-semibold">{statusTitle}</div><div className="mt-1 text-sm"><CustomerTag customerId={data.customer?.id} customer={data.customer}/>{data.customer?.name || tr("Cliente verificato")}</div><div className="text-xs text-muted-foreground">{data.customer?.email || ""} {data.customer?.phone ? `· ${data.customer.phone}` : ""}</div></div></div>
      {completed && gate.completed_at && <p className="mt-3 text-xs text-emerald-500">{tr("Ingresso registrato:")} {new Date(gate.completed_at).toLocaleString(uiLocale())}</p>}
    </div>

    <div className="grid gap-3 sm:grid-cols-2">
      <Mini title={tr("Appuntamento di oggi")} value={a ? `${appointmentServiceLabel(a)} · ${new Date(a.start).toLocaleString(uiLocale())} · ${durationLabel(a.duration_minutes)}` : "Nessun appuntamento previsto oggi"} />
      <Mini title={tr("Artista / Postazione")} value={a ? `${a.artist?.name || "Da assegnare"} · ${a.room?.name || "Postazione da assegnare"}` : "—"} />
      <Mini title={tr("Consenso appuntamento")} value={data.consent?.signed ? "Firmato" : data.consent?.id ? "Da completare" : "Non presente"} />
      <Mini title={tr("Stato anagrafico")} value={ageLabel} />
    </div>

    <div className="rounded-xl border p-4">
      <div className="label-tiny mb-3">{tr("Verifiche canoniche")}</div>
      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <Requirement ok={requirements.appointment_today} label={tr("Appuntamento oggi")} />
        {a?.appointment_type !== "consultation" && <><Requirement ok={requirements.birthdate_known} label={tr("Data di nascita")} /><Requirement ok={requirements.consent_signed} label={tr("Consenso firmato")} /><Requirement ok={requirements.client_identity} label={tr("Documento fronte/retro")} />{gate.minor && <Requirement ok={requirements.guardians_complete} label={tr("Entrambi i tutori")} />}</>}
      </div>
    </div>

    {!ready && gate.blockers?.length > 0 && <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"><div className="font-semibold text-sm">{tr("Da completare prima del check-in")}</div><ul className="mt-2 space-y-1.5 text-sm">{gate.blockers.map((item) => <li key={item.code} className="flex gap-2"><CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive"/><span>{systemText(item.label)}</span></li>)}</ul></div>}

    <details className="group rounded-xl border bg-background/30">
      <summary className="flex cursor-pointer list-none items-center gap-3 p-4">
        <WalletCards className="h-5 w-5 text-primary"/>
        <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{tr("Wallet Cliente")}</span><span className="mt-0.5 block text-xs text-muted-foreground">{tr("Bonus")} {currencyLabel(moneyCurrency)} {Number(data.wallet?.available_bonus_value || 0).toFixed(2)} {tr("· Gift Card")} {currencyLabel(moneyCurrency)} {Number(data.wallet?.available_giftcard_value || 0).toFixed(2)}</span></span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"/>
      </summary>
      <div className="border-t p-4 space-y-2">
        {(data.wallet?.active_bonuses || []).map((item)=><div key={item.id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs"><span><b>{item.label || tr("Bonus")}</b>{item.expires_at&&<span className="ml-2 text-muted-foreground">{tr("scade")} {String(item.expires_at).slice(0,10)}</span>}</span><span>{currencyLabel(item, moneyCurrency)} {Number(item.value||0).toFixed(2)}</span></div>)}
        {(data.wallet?.active_giftcards || []).map((item)=><div key={item.id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs"><span><b>{tr("Gift Card")} {item.code || ""}</b>{item.expires_at&&<span className="ml-2 text-muted-foreground">{tr("scade")} {String(item.expires_at).slice(0,10)}</span>}</span><span>{currencyLabel(item, moneyCurrency)} {Number(item.balance||0).toFixed(2)}</span></div>)}
        {!(data.wallet?.active_bonuses || []).length && !(data.wallet?.active_giftcards || []).length && <p className="text-xs text-muted-foreground">{tr("Nessun benefit disponibile.")}</p>}
        <details className="mt-3 rounded-lg border bg-background/30">
          <summary className="cursor-pointer list-none p-3 text-xs font-semibold">{tr("Storico Wallet recente")}</summary>
          <div className="space-y-2 border-t p-3">
            {(data.bonuses || []).filter((item)=>item.status!=="active" || (item.expires_at && item.expires_at < new Date().toISOString())).map((item)=><div key={"bonus-"+item.id} className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-xs"><span><b>{item.label || tr("Bonus")}</b><small className="block text-muted-foreground">{item.status==="redeemed"?tr("Utilizzato"):item.status==="expired" || (item.expires_at && item.expires_at < new Date().toISOString())?tr("Scaduto"):item.status}</small></span><span>{currencyLabel(item, moneyCurrency)} {Number(item.value||0).toFixed(2)}</span></div>)}
            {(data.giftcards || []).filter((item)=>!["active","partially_used"].includes(item.status) || Number(item.balance||0)<=0 || (item.expires_at && item.expires_at < new Date().toISOString())).map((item)=><div key={"gift-"+item.id} className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-xs"><span><b>{tr("Gift Card")} {item.code || ""}</b><small className="block text-muted-foreground">{item.status==="redeemed" || Number(item.balance||0)<=0?tr("Esaurita"):item.expires_at && item.expires_at < new Date().toISOString()?tr("Scaduta"):item.status}</small></span><span>{tr("Residuo")} {currencyLabel(item, moneyCurrency)} {Number(item.balance||0).toFixed(2)}</span></div>)}
            {!(data.bonuses || []).some((item)=>item.status!=="active" || (item.expires_at && item.expires_at < new Date().toISOString())) && !(data.giftcards || []).some((item)=>!["active","partially_used"].includes(item.status) || Number(item.balance||0)<=0 || (item.expires_at && item.expires_at < new Date().toISOString())) && <p className="text-xs text-muted-foreground">{tr("Nessun utilizzo o scadenza recente.")}</p>}
          </div>
        </details>
      </div>
    </details>

    {ready && !completed && <Button className="w-full min-h-12" onClick={onCheckin} disabled={loading}><CheckCircle2 className="mr-2 h-4 w-4"/>{loading ? tr("Registrazione…") : tr("Conferma check-in")}</Button>}
    {a?.id && canHandlePayments && <CheckoutPanel key={a.id} appointmentId={a.id} onCompleted={onRefresh}/>}
  </div>;
}


function Requirement({ ok, label }) {
  useUiLanguage(); return <div className="flex items-center gap-2"><span className={`grid h-5 w-5 place-items-center rounded-full text-[11px] ${ok ? "bg-emerald-500/15 text-emerald-500" : "bg-destructive/15 text-destructive"}`}>{ok ? "✓" : "!"}</span><span>{label}</span></div>; }
function ProResult({ data, loading, onPresence, onCashClose }) {
  useUiLanguage();
  const present = data.presence?.status === "present";
  return <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="pro-scan-result">
    <div className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4"/>{data.name}</div>
    <div className="mt-1 text-sm text-muted-foreground">{data.role === "artist" ? tr("Tatuatore") : data.role === "front_desk" ? tr("Reception") : data.role === "studio_owner" ? tr("Titolare") : "Manager"}</div>
    <div className="mt-2 text-xs text-emerald-500">{tr("Identità Pro autorizzata in questo Studio")}</div>
    <div className="mt-4 rounded-lg border bg-background/40 p-3">
      <div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">{tr("Presenza attuale")}</span><span className={`rounded-full px-2.5 py-1 text-xs ${present?"bg-emerald-500/15 text-emerald-500":"bg-muted text-muted-foreground"}`}>{present?tr("Presente"):tr("Fuori")}</span></div>
      {data.presence?.since && <p role="status" className="mt-2 text-sm">{present ? tr("Check-in registrato") : tr("Check-out registrato")} · {new Date(data.presence.since).toLocaleString(uiLocale())}</p>}
      <Button className="mt-3 w-full" variant={present?"outline":"default"} disabled={loading} onClick={()=>onPresence(present?"check_out":"check_in")}>{loading ? tr("Registrazione…") : present?tr("Registra check-out"):tr("Registra check-in")}</Button>
    </div>
    <Button className="mt-3 w-full" variant="outline" disabled={loading} onClick={onCashClose}>{tr("Registra chiusura cassa")}</Button>
  </div>;
}
function Mini({ title, value }) {
  useUiLanguage(); return <div className="rounded-xl border p-3"><div className="label-tiny">{title}</div><div className="mt-1 text-sm">{value}</div></div>; }


function AgreementResult({ data, loading, onAccept, onReject }) {
  useUiLanguage();
  const pct = (terms, key) => Number(terms?.[key]?.artist_percentage_bps || 0) / 100;
  const current = data.current?.terms || {};
  const proposed = data.proposed_terms || {};
  const deactivation = proposed.type === "disabled";
  return <div className="mt-5 space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="economic-agreement-preview">
    <div className="flex items-start gap-3"><Handshake className="mt-0.5 h-5 w-5 text-primary"/><div><div className="font-semibold">{tr("Accordo economico ·")} {SERVICE_LABELS[data.service_type || "tattoo"]}</div><p className="mt-1 text-xs text-muted-foreground">{tr("Confronta i valori sul tuo dispositivo prima di approvare. Nessuna modifica è ancora attiva.")}</p></div></div>
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-lg border p-3"><div className="label-tiny">{tr("Attuale · v")}{data.current?.version || "—"}</div><p className="mt-2 text-sm">{current.type==="disabled"?tr("Disattivato"):<>{tr("Cliente Artista")} <b>{pct(current,"artist_client")}%</b><br/>{tr("Cliente Studio")} <b>{pct(current,"studio_client")}%</b></>}</p></div>
      <div className="rounded-lg border border-primary/30 p-3"><div className="label-tiny text-primary">{tr("Proposto")}</div><p className="mt-2 text-sm">{deactivation?tr("Disattivazione dell'accordo"):<>{tr("Cliente Artista")} <b>{pct(proposed,"artist_client")}%</b><br/>{tr("Cliente Studio")} <b>{pct(proposed,"studio_client")}%</b></>}</p></div>
    </div>
    {data.reason&&<p className="rounded-lg border p-3 text-xs text-muted-foreground"><b>{tr("Motivo:")}</b> {data.reason}</p>}
    <p className="text-xs text-muted-foreground">{tr("Se accetti, la nuova versione varrà solo per i futuri incassi. Gli incassi già consolidati mantengono la loro versione storica.")}</p>
    <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={onReject} disabled={loading}>{tr("Rifiuta")}</Button><Button onClick={onAccept} disabled={loading}><CheckCircle2 className="mr-2 h-4 w-4"/>{loading?tr("Conferma…"):tr("Accetta e attiva")}</Button></div>
  </div>;
}




